import React, { useState, useEffect } from 'react';
import { Search, Plus, Filter, ChevronDown, ChevronRight, AlertCircle, CheckCircle, Clock, Lightbulb, FileText, BookOpen, Bug } from 'lucide-react';

// Use proxy path for production (HTTPS) or direct for local development
const useProxy = window.location.protocol === 'https:';
const API_BASE = useProxy ? '/api/projects/tracking' : 'http://localhost:8010/tracking';

const TrackingSystem = () => {
  const [activeTab, setActiveTab] = useState('bugs');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [stats, setStats] = useState({});

  // Fetch items when tab changes
  useEffect(() => {
    fetchItems();
    fetchStats();
  }, [activeTab]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/${activeTab}`);
      const data = await response.json();
      setItems(data);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/${activeTab}/stats/summary`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const createItem = async (itemData) => {
    try {
      const response = await fetch(`${API_BASE}/${activeTab}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemData)
      });
      if (response.ok) {
        fetchItems();
        fetchStats();
        setShowAddForm(false);
      }
    } catch (error) {
      console.error('Error creating item:', error);
    }
  };

  const updateItemStatus = async (id, status) => {
    try {
      const response = await fetch(`${API_BASE}/${activeTab}/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (response.ok) {
        fetchItems();
        fetchStats();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      const response = await fetch(`${API_BASE}/${activeTab}/${id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        fetchItems();
        fetchStats();
      }
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const filteredItems = items.filter(item => {
    const matchesSearch = item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.summary?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || item.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const tabs = [
    { id: 'bugs', label: 'Bugs', icon: Bug, color: 'red' },
    { id: 'ideas', label: 'Ideas', icon: Lightbulb, color: 'yellow' },
    { id: 'features', label: 'Features', icon: FileText, color: 'blue' },
    { id: 'research', label: 'Research', icon: BookOpen, color: 'purple' }
  ];

  return (
    <div className="tracking-system">
      <div className="tracking-header">
        <h1>Tracking System</h1>
        <p>Track bugs, ideas, features, and research across all sessions</p>
      </div>

      {/* Stats Summary */}
      <div className="stats-grid">
        {Object.entries(stats).map(([key, value]) => {
          // Only render numeric values to avoid React object rendering error
          if (typeof value === 'number') {
            return (
              <div key={key} className="stat-card">
                <div className="stat-label">{key.replace(/_/g, ' ')}:</div>
                <div className="stat-value">{value}</div>
              </div>
            );
          }
          return null;
        })}
      </div>

      {/* Tabs */}
      <div className="tracking-tabs">
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${tab.color}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="tracking-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          {activeTab === 'bugs' && (
            <>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </>
          )}
          {activeTab === 'ideas' && (
            <>
              <option value="open">Open</option>
              <option value="under_consideration">Under Consideration</option>
              <option value="planned">Planned</option>
              <option value="implemented">Implemented</option>
              <option value="rejected">Rejected</option>
            </>
          )}
          {activeTab === 'features' && (
            <>
              <option value="active">Active</option>
              <option value="deprecated">Deprecated</option>
            </>
          )}
          {activeTab === 'research' && (
            <>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </>
          )}
        </select>
        <button className="add-button" onClick={() => setShowAddForm(!showAddForm)}>
          <Plus size={18} />
          Add {activeTab.slice(0, -1)}
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && <AddForm type={activeTab} onSubmit={createItem} onCancel={() => setShowAddForm(false)} />}

      {/* Items List */}
      <div className="items-list">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : filteredItems.length === 0 ? (
          <div className="empty-state">No {activeTab} found</div>
        ) : (
          filteredItems.map(item => (
            <ItemCard
              key={item.id}
              item={item}
              type={activeTab}
              expanded={expandedItem === item.id}
              onToggle={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
              onStatusChange={(status) => updateItemStatus(item.id, status)}
              onDelete={() => deleteItem(item.id)}
            />
          ))
        )}
      </div>

      <style jsx>{`
        .tracking-system {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .tracking-header {
          margin-bottom: 30px;
        }

        .tracking-header h1 {
          font-size: 28px;
          margin-bottom: 8px;
          color: #1a1a1a;
        }

        .tracking-header p {
          color: #666;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 15px;
          margin-bottom: 25px;
        }

        .stat-card {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 15px;
        }

        .stat-label {
          font-size: 12px;
          color: #666;
          text-transform: capitalize;
          margin-bottom: 5px;
        }

        .stat-value {
          font-size: 24px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .tracking-tabs {
          display: flex;
          gap: 10px;
          margin-bottom: 20px;
          border-bottom: 2px solid #e0e0e0;
        }

        .tab-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          border: none;
          background: none;
          cursor: pointer;
          font-size: 15px;
          color: #666;
          border-bottom: 3px solid transparent;
          transition: all 0.2s;
        }

        .tab-button:hover {
          color: #1a1a1a;
        }

        .tab-button.active {
          color: #1a1a1a;
          font-weight: 600;
        }

        .tab-button.active.red {
          border-bottom-color: #ef4444;
        }

        .tab-button.active.yellow {
          border-bottom-color: #f59e0b;
        }

        .tab-button.active.blue {
          border-bottom-color: #3b82f6;
        }

        .tab-button.active.purple {
          border-bottom-color: #8b5cf6;
        }

        .tracking-toolbar {
          display: flex;
          gap: 15px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .search-box {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 1;
          min-width: 250px;
          padding: 10px 15px;
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
        }

        .search-box input {
          border: none;
          outline: none;
          flex: 1;
          font-size: 14px;
        }

        .filter-select {
          padding: 10px 15px;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          cursor: pointer;
        }

        .add-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }

        .add-button:hover {
          background: #2563eb;
        }

        .items-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .loading, .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #666;
          font-size: 16px;
        }
      `}</style>
    </div>
  );
};

// Item Card Component
const ItemCard = ({ item, type, expanded, onToggle, onStatusChange, onDelete }) => {
  const getStatusColor = (status) => {
    const colors = {
      open: '#ef4444',
      in_progress: '#f59e0b',
      resolved: '#10b981',
      closed: '#6b7280',
      under_consideration: '#8b5cf6',
      planned: '#3b82f6',
      implemented: '#10b981',
      rejected: '#ef4444',
      active: '#10b981',
      deprecated: '#6b7280',
      completed: '#10b981',
      archived: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const getSeverityColor = (severity) => {
    const colors = {
      critical: '#dc2626',
      high: '#ef4444',
      medium: '#f59e0b',
      low: '#10b981'
    };
    return colors[severity] || '#6b7280';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: '#dc2626',
      high: '#ef4444',
      medium: '#f59e0b',
      low: '#10b981'
    };
    return colors[priority] || '#6b7280';
  };

  return (
    <div className="item-card">
      <div className="item-header" onClick={onToggle}>
        <div className="item-title-row">
          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          <h3>{item.title}</h3>
          <span className="status-badge" style={{ background: getStatusColor(item.status) }}>
            {item.status?.replace(/_/g, ' ')}
          </span>
        </div>
        <div className="item-meta">
          {item.severity && (
            <span className="meta-badge" style={{ background: getSeverityColor(item.severity) }}>
              {item.severity}
            </span>
          )}
          {item.priority && (
            <span className="meta-badge" style={{ background: getPriorityColor(item.priority) }}>
              {item.priority}
            </span>
          )}
          {item.category && <span className="meta-tag">{item.category}</span>}
          <span className="meta-date">{new Date(item.created_at).toLocaleDateString()}</span>
        </div>
      </div>

      {expanded && (
        <div className="item-details">
          <div className="item-summary">{item.summary}</div>

          {item.description && (
            <div className="item-section">
              <strong>Description:</strong>
              <p>{item.description}</p>
            </div>
          )}

          {item.affected_files && item.affected_files.length > 0 && (
            <div className="item-section">
              <strong>Affected Files:</strong>
              <ul>
                {item.affected_files.map((file, i) => <li key={i}>{file}</li>)}
              </ul>
            </div>
          )}

          {item.steps_to_reproduce && (
            <div className="item-section">
              <strong>Steps to Reproduce:</strong>
              <p>{item.steps_to_reproduce}</p>
            </div>
          )}

          {item.expected_behavior && (
            <div className="item-section">
              <strong>Expected Behavior:</strong>
              <p>{item.expected_behavior}</p>
            </div>
          )}

          {item.findings && (
            <div className="item-section">
              <strong>Findings:</strong>
              <p>{item.findings}</p>
            </div>
          )}

          {item.next_steps && (
            <div className="item-section">
              <strong>Next Steps:</strong>
              <p>{item.next_steps}</p>
            </div>
          )}

          {item.estimated_effort && (
            <div className="item-section">
              <strong>Effort:</strong> {item.estimated_effort} | <strong>Impact:</strong> {item.estimated_impact}
            </div>
          )}

          {item.tags && item.tags.length > 0 && (
            <div className="item-tags">
              {item.tags.map((tag, i) => <span key={i} className="tag">{tag}</span>)}
            </div>
          )}

          <div className="item-actions">
            <select
              value={item.status}
              onChange={(e) => onStatusChange(e.target.value)}
              className="status-select"
            >
              {type === 'bugs' && (
                <>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </>
              )}
              {type === 'ideas' && (
                <>
                  <option value="open">Open</option>
                  <option value="under_consideration">Under Consideration</option>
                  <option value="planned">Planned</option>
                  <option value="implemented">Implemented</option>
                  <option value="rejected">Rejected</option>
                </>
              )}
              {type === 'features' && (
                <>
                  <option value="active">Active</option>
                  <option value="deprecated">Deprecated</option>
                </>
              )}
              {type === 'research' && (
                <>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="archived">Archived</option>
                </>
              )}
            </select>
            <button onClick={onDelete} className="delete-button">Delete</button>
          </div>
        </div>
      )}

      <style jsx>{`
        .item-card {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          overflow: hidden;
        }

        .item-header {
          padding: 16px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .item-header:hover {
          background: #f9fafb;
        }

        .item-title-row {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 8px;
        }

        .item-title-row h3 {
          flex: 1;
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #1a1a1a;
        }

        .status-badge {
          padding: 4px 12px;
          border-radius: 12px;
          color: white;
          font-size: 12px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .item-meta {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-left: 30px;
          flex-wrap: wrap;
        }

        .meta-badge {
          padding: 3px 10px;
          border-radius: 10px;
          color: white;
          font-size: 11px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .meta-tag {
          padding: 3px 10px;
          background: #f3f4f6;
          border-radius: 10px;
          font-size: 11px;
          color: #6b7280;
          text-transform: capitalize;
        }

        .meta-date {
          font-size: 12px;
          color: #9ca3af;
        }

        .item-details {
          padding: 0 16px 16px 16px;
          border-top: 1px solid #f3f4f6;
        }

        .item-summary {
          padding: 16px 0;
          color: #4b5563;
          line-height: 1.6;
        }

        .item-section {
          margin-bottom: 16px;
        }

        .item-section strong {
          display: block;
          margin-bottom: 8px;
          color: #1a1a1a;
        }

        .item-section p {
          margin: 0;
          color: #4b5563;
          line-height: 1.6;
        }

        .item-section ul {
          margin: 0;
          padding-left: 20px;
          color: #4b5563;
        }

        .item-tags {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-bottom: 16px;
        }

        .tag {
          padding: 4px 12px;
          background: #eff6ff;
          color: #3b82f6;
          border-radius: 12px;
          font-size: 12px;
        }

        .item-actions {
          display: flex;
          gap: 12px;
          margin-top: 16px;
        }

        .status-select {
          padding: 8px 12px;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          font-size: 14px;
          background: white;
          cursor: pointer;
        }

        .delete-button {
          padding: 8px 16px;
          background: #ef4444;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: background 0.2s;
        }

        .delete-button:hover {
          background: #dc2626;
        }
      `}</style>
    </div>
  );
};

// Add Form Component
const AddForm = ({ type, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({});

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <div className="add-form">
      <form onSubmit={handleSubmit}>
        <h3>Add New {type.slice(0, -1)}</h3>

        <input
          type="text"
          placeholder="Title"
          required
          onChange={(e) => updateField('title', e.target.value)}
        />

        <textarea
          placeholder="Summary"
          required
          onChange={(e) => updateField('summary', e.target.value)}
        />

        {type === 'bugs' && (
          <>
            <select onChange={(e) => updateField('severity', e.target.value)} required>
              <option value="">Select Severity</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <textarea placeholder="Steps to Reproduce" onChange={(e) => updateField('steps_to_reproduce', e.target.value)} />
            <textarea placeholder="Expected Behavior" onChange={(e) => updateField('expected_behavior', e.target.value)} />
          </>
        )}

        {type === 'ideas' && (
          <>
            <select onChange={(e) => updateField('category', e.target.value)} required>
              <option value="">Select Category</option>
              <option value="feature">Feature</option>
              <option value="improvement">Improvement</option>
              <option value="optimization">Optimization</option>
              <option value="refactoring">Refactoring</option>
              <option value="other">Other</option>
            </select>
            <select onChange={(e) => updateField('priority', e.target.value)} required>
              <option value="">Select Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
            <select onChange={(e) => updateField('estimated_effort', e.target.value)}>
              <option value="">Estimated Effort</option>
              <option value="small">Small</option>
              <option value="medium">Medium</option>
              <option value="large">Large</option>
            </select>
            <select onChange={(e) => updateField('estimated_impact', e.target.value)}>
              <option value="">Estimated Impact</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </>
        )}

        {type === 'features' && (
          <>
            <select onChange={(e) => updateField('category', e.target.value)} required>
              <option value="">Select Category</option>
              <option value="core">Core</option>
              <option value="api">API</option>
              <option value="ui">UI</option>
              <option value="integration">Integration</option>
              <option value="other">Other</option>
            </select>
            <textarea placeholder="Description" onChange={(e) => updateField('description', e.target.value)} />
          </>
        )}

        {type === 'research' && (
          <>
            <select onChange={(e) => updateField('category', e.target.value)} required>
              <option value="">Select Category</option>
              <option value="algorithm">Algorithm</option>
              <option value="performance">Performance</option>
              <option value="data_analysis">Data Analysis</option>
              <option value="experiment">Experiment</option>
              <option value="investigation">Investigation</option>
              <option value="other">Other</option>
            </select>
            <textarea placeholder="Findings" onChange={(e) => updateField('findings', e.target.value)} />
            <textarea placeholder="Next Steps" onChange={(e) => updateField('next_steps', e.target.value)} />
          </>
        )}

        <div className="form-actions">
          <button type="submit" className="submit-btn">Create</button>
          <button type="button" onClick={onCancel} className="cancel-btn">Cancel</button>
        </div>
      </form>

      <style jsx>{`
        .add-form {
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
        }

        .add-form h3 {
          margin: 0 0 20px 0;
          font-size: 18px;
          color: #1a1a1a;
        }

        .add-form input,
        .add-form textarea,
        .add-form select {
          width: 100%;
          padding: 10px;
          margin-bottom: 12px;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
        }

        .add-form textarea {
          min-height: 80px;
          resize: vertical;
        }

        .form-actions {
          display: flex;
          gap: 12px;
          margin-top: 16px;
        }

        .submit-btn {
          padding: 10px 24px;
          background: #3b82f6;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        }

        .submit-btn:hover {
          background: #2563eb;
        }

        .cancel-btn {
          padding: 10px 24px;
          background: #f3f4f6;
          color: #6b7280;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
        }

        .cancel-btn:hover {
          background: #e5e7eb;
        }
      `}</style>
    </div>
  );
};

export default TrackingSystem;
